import {Component, Inject, OnDestroy} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UserCardService} from "../../service/user-card.service";
import {SharedConstants} from "../../utilityClass/sharedConstants";
import {INCORRECT_COMMISSION, INVALID_COMMISSION} from "../../constants/errorMessages";
import {UserCardCommissionDialogData} from "../../interface/UserCardCommissionDialogData";

@Component({
  selector: 'app-user-card-commission-edit',
  templateUrl: './user-card-commission-edit.component.html',
  styleUrl: './user-card-commission-edit.component.scss'
})
export class UserCardCommissionEditComponent implements OnDestroy {

  form!: FormGroup;
  errorMessage: string | null = null;
  commissionReadOnly: boolean = false;

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    protected dialogRef: MatDialogRef<UserCardCommissionEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserCardCommissionDialogData,
    private userCardService: UserCardService,
    private formBuilder: FormBuilder,
  ) {
    this.initForm();
    this.subscribeToCommissionChanges();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  edit(): void {
    this.errorMessage = null;

    if (this.form.valid) {
      const commission: number = this.form.value.commission!;
      const isCommissionChanged: boolean = commission !== this.data.commission;
      if (isCommissionChanged) {
        this.editCommissionById(commission);
      } else {
        this.dialogRef.close({commission: commission, id: this.data.id});
      }
    } else {
      this.errorMessage = INVALID_COMMISSION;
    }
  }

  private editCommissionById(commission: number) {
    this.userCardService.editCommissionById(this.data.id, commission).subscribe({
      next: () => {
        this.dialogRef.close({commission: commission, id: this.data.id});
      },
      error: () => {
        this.errorMessage = INCORRECT_COMMISSION;
      }
    });
  }

  protected isButtonDisabled() {
    return this.form.invalid || this.commissionReadOnly;
  }

  private commissionValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (isNaN(value)) {
      this.errorMessage = INVALID_COMMISSION;
      return { invalidCommission: true };
    }

    if (value < 1) {
      return { commissionTooLow: true };
    } else if (value > 100) {
      return { commissionTooHigh: true };
    }
    return null;
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      commission: [this.data.commission, [Validators.required, this.commissionValidator]]
    });
  }

  private subscribeToCommissionChanges(): void {
    this.form.get('commission')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => { this.handleCommissionValueChange(value); }
      });
  }

  private handleCommissionValueChange(value: number): void {
    this.commissionReadOnly = value < 1 || value > 100;
    this.errorMessage = this.commissionReadOnly ? INCORRECT_COMMISSION : null;
  }

  protected readonly SharedConstants = SharedConstants;
}
